import { useEffect, useRef, useState } from 'react';
import { useImmer } from 'use-immer';
import classNames from 'classnames';
import useStatuses from '../../hooks/useStatuses';
import useGlobalParams from '../../hooks/useGlobalParams';
import useUsers from '../../hooks/useUsers';
import LEAD_FIELDS from '../../utils/leadFields';
import { Trans as T, useTranslation } from 'react-i18next';

import LeadAssigneeDropdown from './LeadAssigneeDropdown';
import LeadStatusDropdown from './LeadStatusDropdown';
import Form from 'react-bootstrap/Form';
import Dropdown from 'react-bootstrap/Dropdown';

import './FilterDropdown.scss';

// NO NAME FOR NOW
const FILTER_FIELDS = ['email', 'country', 'state', 'city', 'zip_code', 'status_id', 'assigned_user_id'];

const TEXT_FIELDS = ['email', 'country', 'state', 'city', 'zip_code'];

export default function FilterDropdown({ toggle, children, filters, index, setFilters, align }) {
  const [show, setShow] = useState(false);
  const [editingFilter, setEditingFilter] = useImmer({});
  
  const { organizationId, categoryId } = useGlobalParams();
  const { data: statuses } = useStatuses({ categoryId });
  const { data: users } = useUsers({ organizationId, categoryId });

  const { t } = useTranslation();
  
  const defaultInputRef = useRef();
  
  useEffect(() => {
    if (show) {
      if (typeof index === 'number') {
        setEditingFilter(filters[index]);
      }
      else {
        // TODO -> if the selection is STATUS, then match and value need to be changed
        setEditingFilter({
          field: FILTER_FIELDS.find(field => !filters.find(filter => filter.field === field)), 
          match: 'contains',
        });
      }

      defaultInputRef.current?.focus();
    }
    else {
      setEditingFilter({});
    }
  }, [show, filters, index, setEditingFilter]);


  // TODO -> this might create bugs, might be better to check the e.originalEvent:
  const onToggleShow = (_, e) => {
    // if the source is the inner status select, do not close
    if (e.source !== 'select') {
      setShow(!show);
    }
  }

  const onSubmit = e => {
    e.preventDefault();

    setFilters(draft => {
      if (typeof index !== 'number') {
        draft.push(editingFilter);
      }
      else {
        draft[index] = editingFilter;
      }
    });

    setShow(false);
  }

  const onChangeField = e => {
    const field = e.target.value;

    setEditingFilter(draft => {
      draft.field = field;

      if (field === 'status_id') {
        draft.match = 'is';
        draft.value = [];
      }
      else if (field === 'assigned_user_id') {
        draft.match = 'is';
        draft.value = null;
      }
      // if previous filter was status id, clear the value:
      else if (editingFilter.field === 'status_id') {
        draft.value = '';
      }
    });
  }

  const renderMatch = () => {
    if (TEXT_FIELDS.includes(editingFilter.field)) {
      return (
        <Form.Select className='match' value={editingFilter.match} onChange={e => setEditingFilter(draft => { draft.match = e.target.value })}>
          { ['contains', 'is'].map(match => <option key={match} value={match}>{ t(match) }</option> )}
        </Form.Select>
      );
    }

    else {
      return <div className='match'>is</div>;
    }
  }

  const onChangeStatus = value => {
    setEditingFilter(draft => {
      draft.value = value;
    });
  }
  
  const emptyValue = () => {
    if (TEXT_FIELDS.includes(editingFilter.field)) {
      return !editingFilter.value?.trim().length;
    }
    else if (editingFilter.field === 'status_id') { // if status
      return editingFilter.value.length === 0;
    }
    else if (editingFilter.field === 'assigned_user_id') {
      return !editingFilter.value;
    }
  }

  const renderInput = () => {
    if (TEXT_FIELDS.includes(editingFilter.field)) {
      return <Form.Control minLength={1} className='input-value' type='text' value={editingFilter.value || ''} placeholder='Enter filter value...' onChange={e => setEditingFilter(draft => { draft.value = e.target.value })} ref={defaultInputRef} />
    }
    else if (editingFilter.field === 'status_id') {
      return <LeadStatusDropdown multiple statuses={statuses} onChange={onChangeStatus} selectedIds={editingFilter.value} placeholder='Select a status' />
    }
    else if (editingFilter.field === 'assigned_user_id') {
      return <LeadAssigneeDropdown users={users} selectedUserId={editingFilter.value} onChange={id => setEditingFilter(draft => { draft.value = id })} type='large' hideUnassigned  placeholder='Select a user' />
    }
  }

  return (
    <Dropdown show={show} onToggle={onToggleShow} className='__filter-dropdown' align={align}>
      <Dropdown.Toggle as={toggle}>{ children }</Dropdown.Toggle>
      <Dropdown.Menu>
        <div className='transition-container'>
          <Form onSubmit={onSubmit}>
            <div className='field-match'>
              <Form.Select className='field' value={editingFilter.field} onChange={onChangeField}>
                { FILTER_FIELDS.map(field => (
                  <option key={field} value={field} disabled={filters.some(f => f.field === field) && editingFilter.field !== field && filters[index]?.field !== field }>
                    <T>{ LEAD_FIELDS[field].label }</T>
                  </option>
                ))}
              </Form.Select>
              { renderMatch() }
            </div>

            { renderInput() }
            
            { TEXT_FIELDS.includes(editingFilter.field) && editingFilter.value?.trim().length > 0 && (
              <p className={classNames('explanation', { visible: editingFilter.value?.trim().length > 0 })}>
                Display all leads whose {LEAD_FIELDS[editingFilter.field].label} {editingFilter.match === 'is' ? 'exactly matches' : 'contains the text'}: <span className='value'>{editingFilter.value.trim()}</span>
              </p>
            )}

            <div className='actions'>
              <button type='button' className='close' onClick={() => setShow(false)}><T>Close</T></button>
              <button type='submit' disabled={emptyValue()}><T>{ typeof index === 'number' ? 'Edit' : 'Add' } Filter</T></button>
            </div>
          </Form>
        </div>
      </Dropdown.Menu>
    </Dropdown>
  )
}
